import React, { useEffect, useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
// Facebook login temporarily hidden
// import FacebookLogin from 'react-facebook-login';
// import { SOCIAL_LOGIN_CONFIG } from '../config/socialLogin';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaGoogle } from 'react-icons/fa';

// Where to send the user after a successful ATOZAS login (same as other logins).
const ATOZAS_RETURN_TO = '/services';



const SocialLogin = ({ onLoginSuccess, termsAccepted = false, onBlockedUser }) => {
  const navigate = useNavigate();

  // ATOZAS SSO is only shown when the backend reports it enabled.
  const [atozasEnabled, setAtozasEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`/auth/atozas/status?_=${Date.now()}`, {
        withCredentials: true,
        headers: { 'Cache-Control': 'no-store', Pragma: 'no-cache' },
      })
      .then((res) => {
        if (cancelled || !res.data || !res.data.enabled) return;
        setAtozasEnabled(true);
        // Optional auto-redirect straight into ATOZAS when configured and the
        // user is not already logged in. Skip when we just landed here after an
        // SSO error so we do not loop.
        const params = new URLSearchParams(window.location.search);
        const ssoFailed = params.get('sso') === 'atozas' && !!params.get('error');
        if (res.data.autoRedirect && !localStorage.getItem('token') && !ssoFailed) {
          window.location.href = `/auth/atozas?returnTo=${encodeURIComponent(
            ATOZAS_RETURN_TO
          )}`;
        }
      })
      .catch(() => {
        // SSO disabled / route not mounted -> simply don't show the button.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAtozasLogin = () => {
    if (!termsAccepted) {
      toast.error('Please agree to the Terms of Service and Privacy Policy to continue');
      return;
    }
    // Full-page navigation so the backend can set the session cookie and
    // perform the OIDC redirect. Tokens stay entirely on the backend.
    window.location.href = `/auth/atozas?returnTo=${encodeURIComponent(
      ATOZAS_RETURN_TO
    )}`;
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    if (!termsAccepted) {
      toast.error('Please agree to the Terms of Service and Privacy Policy to continue');
      return;
    }

    try {
      // Decode the JWT token to get user info
      const decoded = JSON.parse(atob(credentialResponse.credential.split('.')[1]));

      // Parse name with fallback for accounts without given_name/family_name
      let firstName = decoded.given_name || '';
      let lastName = decoded.family_name || '';

      // If given_name/family_name missing, split full name
      if ((!firstName || !lastName) && decoded.name) {
        const nameParts = decoded.name.trim().split(' ');
        if (!firstName) firstName = nameParts[0] || '';
        if (!lastName) lastName = nameParts.slice(1).join(' ') || '';
      }

      // Final fallback to ensure we always have names
      if (!firstName) firstName = 'Google';
      if (!lastName) lastName = 'User';

      const userData = {
        email: decoded.email,
        firstName,
        lastName,
        profilePhoto: decoded.picture,
        googleId: decoded.sub
      };

      // Log the data being sent (for debugging)
      console.log('Sending Google login data:', {
        hasEmail: !!userData.email,
        hasFirstName: !!userData.firstName,
        hasLastName: !!userData.lastName,
        hasGoogleId: !!userData.googleId,
        firstName: userData.firstName,
        lastName: userData.lastName
      });

      const response = await axios.post('/api/auth/google-login', userData);

      if (response.data.success) {
        localStorage.setItem('token', response.data.token);
        toast.success(response.data.msg);

        if (response.data.isNewUser) {
          // New user - redirect to profile page
          navigate('/profile');
        } else {
          // Existing user - redirect to services page
          navigate('/services');
        }

        if (onLoginSuccess) {
          onLoginSuccess(response.data);
        }
      }
    } catch (error) {
      console.error('Google login error:', error);
      if (error.response?.status === 403 && error.response?.data?.blocked) {
        if (onBlockedUser) {
          onBlockedUser();
        } else {
          toast.error('Your account has been blocked by admin. Please contact support.');
        }
      } else {
        toast.error(error.response?.data?.msg || 'Google login failed');
      }
    }
  };

  const handleGoogleError = () => {
    toast.error('Google login failed');
  };

  const responseFacebook = async (response) => {
    // console.log('Facebook response:', response); // Disabled for security
    
    if (!termsAccepted) {
      toast.error('Please agree to the Terms of Service and Privacy Policy to continue');
      return;
    }
    
    if (response.accessToken) {
      try {
        // Check if we have all required fields
        if (!response.email || !response.first_name || !response.last_name || !response.userID) {
          console.error('Missing Facebook fields:', {
            email: response.email,
            firstName: response.first_name,
            lastName: response.last_name,
            userID: response.userID
          });
          toast.error('Facebook login failed: Missing required information. Please check your Facebook permissions.');
          return;
        }

        // Get user info from Facebook
        const userData = {
          email: response.email,
          firstName: response.first_name,
          lastName: response.last_name,
          profilePhoto: response.picture?.data?.url || '',
          facebookId: response.userID
        };

        // console.log('Sending user data to backend:', userData); // Disabled for security

        const apiResponse = await axios.post('/api/auth/facebook-login', userData);

        if (apiResponse.data.success) {
          localStorage.setItem('token', apiResponse.data.token);
          toast.success(apiResponse.data.msg);

          if (apiResponse.data.isNewUser) {
            // New user - redirect to profile page
            navigate('/profile');
          } else {
            // Existing user - redirect to services page
            navigate('/services');
          }

          if (onLoginSuccess) {
            onLoginSuccess(apiResponse.data);
          }
        }
      } catch (error) {
        console.error('Facebook login error:', error);
        if (error.response?.status === 403 && error.response?.data?.blocked) {
          if (onBlockedUser) {
            onBlockedUser();
          } else {
            toast.error('Your account has been blocked by admin. Please contact support.');
          }
        } else if (error.response?.data?.msg) {
          toast.error(error.response.data.msg);
        } else {
          toast.error('Facebook login failed. Please try again.');
        }
      }
    } else {
      console.error('Facebook login failed - no access token');
      toast.error('Facebook login failed. Please try again.');
    }
  };

  return (
    <div className="social-login-container">
      <div className="social-login-divider">
        <span>OR</span>
      </div>

      <div className={`social-buttons ${!termsAccepted ? 'disabled' : ''}`}>
        <div className={`social-button-wrapper ${!termsAccepted ? 'disabled' : ''}`}>
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            useOneTap={false}
            theme="outline"
            size="large"
            text="continue_with"
            shape="rectangular"
            logo_alignment="left"
            width="100%"
          />
        </div>

        {/* ATOZAS Cross-Domain SSO - only when enabled on the backend */}
        {atozasEnabled && (
          <div className={`social-button-wrapper atozas-button-wrapper ${!termsAccepted ? 'disabled' : ''}`}>
            <button
              type="button"
              className={`atozas-login-btn ${!termsAccepted ? 'disabled' : ''}`}
              onClick={handleAtozasLogin}
              disabled={!termsAccepted}
              style={{
                width: '100%',
                padding: '10px 16px',
                marginTop: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                background: '#0b5fff',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                fontSize: '15px',
                fontWeight: 500,
                cursor: termsAccepted ? 'pointer' : 'not-allowed',
                opacity: termsAccepted ? 1 : 0.6,
              }}
            >
              Continue with ATOZAS
            </button>
          </div>
        )}

        {/* Facebook Login - Hidden as per user request */}
        {/* <div className={`social-button-wrapper ${!termsAccepted ? 'disabled' : ''}`}>
          <FacebookLogin
            appId={SOCIAL_LOGIN_CONFIG.FACEBOOK_APP_ID}
            autoLoad={false}
            fields="name,email,picture,first_name,last_name"
            scope="public_profile,email"
            callback={responseFacebook}
            cssClass={`facebook-login-btn ${!termsAccepted ? 'disabled' : ''}`}
            icon={<FaFacebook />}
            textButton="Continue with Facebook"
          />
        </div> */}
      </div>
    </div>
  );
};

export default SocialLogin; 